/**
 * Entitlement checking utilities for the frontend.
 *
 * NO legacy-flag fallback. Pure array-includes against `user.entitlements`.
 * If a user's entitlements array is empty or missing, hasEntitlement returns
 * false - they have no access through this path. The migration script seeds
 * entitlements from legacy flags for existing users before any new surface
 * starts gating on this.
 *
 * Function name kept as `hasSubscription` for ergonomic call sites ("does the
 * user have a subscription to this product?") even though the underlying field
 * is `entitlements`. See constants/entitlements.ts for the rename rationale.
 */

import { EntitlementKey, ENTITLEMENTS } from '../constants/entitlements';

// Minimal user-shape interface — accepts any object with optional entitlements
// + legacy flat flags. Matches AuthContext User but kept loose so tests/mocks
// don't need the full shape.
interface UserLike {
  entitlements?: string[] | null;
  // Legacy per-symbol options flags (still consulted as fallback by helpers).
  spxSignalsEnabled?: boolean;
  spySignalsEnabled?: boolean;
  qqqSignalsEnabled?: boolean;
  iwmSignalsEnabled?: boolean;
  esSignalsEnabled?: boolean;
  mesSignalsEnabled?: boolean;
  gcSignalsEnabled?: boolean;
  clSignalsEnabled?: boolean;
  nqSignalsEnabled?: boolean;
  mnqSignalsEnabled?: boolean;
  indexSignalsEnabled?: boolean;
  notchesEnabled?: boolean;
  optionsAutoTradingEnabled?: boolean;
  algoTradingEnabled?: boolean;
}

export type SymbolName = 'SPX' | 'SPY' | 'QQQ' | 'IWM' | 'ES' | 'MES' | 'GC' | 'CL' | 'NQ' | 'MNQ';
export type AutoTraderKind = 'tastytrade' | 'alpaca';

/**
 * The Boring Bucket section gate (Founder's Trades, Mentoring, Capital Plan).
 * Mirror of canAccessBoringBucket in api/auth/src/entitlements.js.
 */
export function canAccessBoringBucket(user: UserLike | null | undefined): boolean {
  return hasSubscription(user, ENTITLEMENTS.BORING_BUCKET);
}

/**
 * Does the user have access to the named entitlement?
 *
 * Returns true ONLY if the entitlement string appears in the user's
 * entitlements array. No fallback to legacy flat flags (spxSignalsEnabled,
 * notchesEnabled, etc.) - those are owned by V2-pattern readers which keep
 * their existing checks unchanged.
 */
export function hasSubscription(user: UserLike | null | undefined, key: EntitlementKey): boolean {
  if (!user) return false;
  const list = user.entitlements;
  if (!Array.isArray(list)) return false;
  return list.includes(key);
}

/**
 * Convenience: does the user have ANY of the supplied entitlements? Useful
 * for nav rendering where a section should show if user has any sub-product.
 */
export function hasAnySubscription(user: UserLike | null | undefined, keys: EntitlementKey[]): boolean {
  if (!user || !Array.isArray(user.entitlements)) return false;
  const set = new Set(user.entitlements);
  return keys.some((k) => set.has(k));
}

/**
 * Convenience: does the user have ALL of the supplied entitlements? Useful
 * for compound checks ("Premier features require both X and Y entitlements").
 */
export function hasAllSubscriptions(user: UserLike | null | undefined, keys: EntitlementKey[]): boolean {
  if (!user || !Array.isArray(user.entitlements)) return false;
  const set = new Set(user.entitlements);
  return keys.every((k) => set.has(k));
}

/* ─── Centralized access helpers ────────────────────────────────────────────
 *
 * Replace ad-hoc `hasSubscription(...) || user.xxxFlag` chains at gate sites
 * with these. Changing the access rule for a product = edit one function here,
 * not every consumer. Mirror of api/auth/src/entitlements.js helpers - keep
 * the rules in sync.
 */

// Per-symbol options access. Entitlement first, legacy flat-flag fallback.
// SPY/ES/MES also accept the legacy `indexSignalsEnabled` umbrella flag.
export function canAccessSymbol(user: UserLike | null | undefined, symbol: SymbolName): boolean {
  if (!user) return false;
  switch (symbol) {
    case 'SPX': return hasSubscription(user, ENTITLEMENTS.SIGNALS_OPTIONS_SPX) || !!user.spxSignalsEnabled;
    case 'SPY': return hasSubscription(user, ENTITLEMENTS.SIGNALS_OPTIONS_SPY) || !!user.spySignalsEnabled || !!user.indexSignalsEnabled;
    case 'QQQ': return hasSubscription(user, ENTITLEMENTS.SIGNALS_OPTIONS_QQQ) || !!user.qqqSignalsEnabled || !!user.indexSignalsEnabled;
    case 'IWM': return hasSubscription(user, ENTITLEMENTS.SIGNALS_OPTIONS_IWM) || !!user.iwmSignalsEnabled || !!user.indexSignalsEnabled;
    case 'ES':  return hasSubscription(user, ENTITLEMENTS.SIGNALS_OPTIONS_ES)  || !!user.esSignalsEnabled  || !!user.indexSignalsEnabled;
    case 'MES': return hasSubscription(user, ENTITLEMENTS.SIGNALS_OPTIONS_MES) || !!user.mesSignalsEnabled || !!user.indexSignalsEnabled;
    case 'GC':  return hasSubscription(user, ENTITLEMENTS.SIGNALS_OPTIONS_GC)  || !!user.gcSignalsEnabled;
    case 'CL':  return hasSubscription(user, ENTITLEMENTS.SIGNALS_OPTIONS_CL)  || !!user.clSignalsEnabled;
    // NQ + MNQ (2026-09-17 add): bundle-only from day one. Grant if any
    // futures-bundle signal is on the user record so existing
    // futures-options-research subscribers see them without a re-materialize.
    case 'NQ':  return hasSubscription(user, ENTITLEMENTS.SIGNALS_OPTIONS_NQ)
      || hasSubscription(user, ENTITLEMENTS.SIGNALS_OPTIONS_MES)
      || !!user.nqSignalsEnabled || !!user.mesSignalsEnabled || !!user.indexSignalsEnabled;
    case 'MNQ': return hasSubscription(user, ENTITLEMENTS.SIGNALS_OPTIONS_MNQ)
      || hasSubscription(user, ENTITLEMENTS.SIGNALS_OPTIONS_MES)
      || !!user.mnqSignalsEnabled || !!user.mesSignalsEnabled || !!user.indexSignalsEnabled;
    default:    return false;
  }
}

export function canAccessAnySymbol(user: UserLike | null | undefined, symbols: SymbolName[]): boolean {
  return symbols.some((s) => canAccessSymbol(user, s));
}

// Master Notches platform gate (everything else is downstream of this).
export function canAccessNotches(user: UserLike | null | undefined): boolean {
  return !!user && (hasSubscription(user, ENTITLEMENTS.NOTCHES) || !!user.notchesEnabled);
}

// Auto-trader access. Mirror of api/auth/src/entitlements.js canAccessAutoTrader.
export function canAccessAutoTrader(user: UserLike | null | undefined, kind: AutoTraderKind): boolean {
  if (!user) return false;
  switch (kind) {
    case 'tastytrade':  return hasSubscription(user, ENTITLEMENTS.AUTO_TRADERS_TASTYTRADE) || !!user.optionsAutoTradingEnabled;
    case 'alpaca':      return hasSubscription(user, ENTITLEMENTS.AUTO_TRADERS_ALPACA)     || !!user.algoTradingEnabled;
    default:            return false;
  }
}
